import { ClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Heading,
  HStack,
  Icon,
  Spacer,
  Text,
  Tooltip,
  useIsMobile,
  VStack,
} from '@dotfile/frontend/shared/design-system';
import { formatDateTime } from '@dotfile/shared/common';
import '@dotfile/shared/data-access-client-portal';
import { ClientPortalCase_ContactCase } from '@dotfile/shared/data-access-client-portal';
import { CaseStatusEnum } from '@dotfile/shared/domain';

import { useSwitchCase } from '../hooks';

export type EntityCardProps = {
  case: ClientPortalCase_ContactCase;
};

export const CaseEntityCard = ({ case: caseEntity }: EntityCardProps) => {
  const [switchCase, { loading }] = useSwitchCase();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const lastActivityAt = formatDateTime(caseEntity.lastActivityAt);

  return (
    <VStack
      align="start"
      width="full"
      spacing="4"
      borderRadius="lg"
      backgroundColor="white"
    >
      <HStack
        h="64px"
        width="full"
        spacing="4"
        border="1px solid"
        borderColor="gray.200"
        borderTop="0px"
        _first={{
          borderTopRadius: 'lg',
          borderTop: '1px solid',
          borderTopColor: 'gray.200',
        }}
        _last={{
          borderBottomRadius: 'lg',
        }}
        padding="4"
      >
        <Heading size="md" color="black" fontWeight="400">
          {caseEntity.name}
        </Heading>
        {!isMobile && (
          <Tooltip
            label={t('onboardings.last_activity_at', {
              ns: 'client-portal',
              defaultValue: `Last activity at ${lastActivityAt}`,
              lastActivityAt,
            })}
          >
            <HStack>
              <Icon color="gray.500" as={ClockIcon} />
              <Text color="gray.500">
                {formatDateTime(caseEntity.lastActivityAt)}
              </Text>
            </HStack>
          </Tooltip>
        )}
        <Spacer />
        {(caseEntity.status === CaseStatusEnum.draft ||
          (caseEntity.status === CaseStatusEnum.open &&
            caseEntity.contactHasActions)) && (
          <Button
            isLoading={loading}
            onClick={async () => {
              await switchCase({
                caseId: caseEntity.id,
              });
            }}
          >
            {t('onboardings.resume', {
              ns: 'client-portal',
              defaultValue: 'Resume',
            })}
          </Button>
        )}
      </HStack>
    </VStack>
  );
};
